import { ScheduledQuery, ScheduledQueryConversation } from '@api';
import { Alert, AlertType } from '@design/components/alert/Alert';
import { CheckBox } from '@design/components/checkbox/CheckBox';
import { Icon } from '@design/components/icon/Icon';
import { Modal } from '@design/components/modal/Modal';
import { useDeleteScheduledQuery } from '@terminal/react-query-hooks/alfred/useScheduledQueryMutations';
import { ToastPosition, useToasts } from '@terminal/stores/use-toasts/useToasts';
import { useChat } from '@terminal/views/alfred/research/use-chat/useChat';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import styles from './DeleteScheduledTaskModal.module.scss';

interface DeleteScheduledTaskModalProps {
  task: ScheduledQuery;
  cadenceSummary: string;
  conversations?: ScheduledQueryConversation[];
  onClose: () => void;
  onDeleted: () => void;
}

export const DeleteScheduledTaskModal = ({
  task,
  cadenceSummary,
  conversations,
  onClose,
  onDeleted,
}: DeleteScheduledTaskModalProps) => {
  const { t } = useTranslation(['chat']);
  const { mutate: deleteQuery, isPending: isDeletingTask } = useDeleteScheduledQuery();
  const [alsoDeleteConversations, setAlsoDeleteConversations] = useState(false);
  const [isDeletingConversations, setIsDeletingConversations] = useState(false);
  const isPending = isDeletingTask || isDeletingConversations;

  const { showToast, removeToast } = useToasts(state => ({
    showToast: state.showToast,
    removeToast: state.removeToast,
  }));

  const handleDelete = () => {
    deleteQuery(task.id, {
      onSuccess: async () => {
        if (alsoDeleteConversations && conversations?.length) {
          setIsDeletingConversations(true);
          // deletePrivateConversation calls the API AND removes the conversation from the Zustand store,
          // which is what HistorySidebar reads from - so the UI updates immediately.
          const results = await Promise.allSettled(
            conversations.map(c => useChat.getState().deletePrivateConversation(c.id))
          );
          setIsDeletingConversations(false);

          const anyFailed = results.some(r => r.status === 'rejected');
          if (anyFailed) {
            const warnId = `task-delete-warn-${Date.now()}`;
            showToast({
              id: warnId,
              position: ToastPosition.TopRight,
              content: (
                <Alert
                  type={AlertType.Error}
                  title={t('chat:scheduledQuerySettings.toastConvDeleteFailedTitle')}
                  message={t('chat:scheduledQuerySettings.toastConvDeleteFailedMessage')}
                  onClose={() => removeToast(warnId)}
                />
              ),
            });
          }
        }

        const toastId = `task-deleted-${Date.now()}`;
        showToast({
          id: toastId,
          position: ToastPosition.TopRight,
          content: (
            <Alert
              type={AlertType.Success}
              title={t('chat:scheduledQuerySettings.toastTaskDeletedTitle')}
              message={t('chat:scheduledQuerySettings.toastTaskDeletedMessage')}
              onClose={() => removeToast(toastId)}
            />
          ),
        });
        onDeleted();
        onClose();
      },
    });
  };

  return (
    <Modal
      isOpen
      title={t('chat:scheduledQuerySettings.deleteModalTitle')}
      onClose={onClose}
      cancelBtnProps={{
        label: t('chat:scheduledQuerySettings.cancel'),
        onClick: onClose,
      }}
      primaryBtnProps={{
        label: t('chat:scheduledQuerySettings.deleteTask'),
        onClick: handleDelete,
        disabled: isPending,
        isLoading: isPending,
        variant: 'critical',
      }}
    >
      <div className={styles.body}>
        <p className={styles.description}>
          {t('chat:scheduledQuerySettings.deleteModalDescription')}
        </p>

        <div className={styles.taskCard}>
          {/* Top section — task info */}
          <div className={styles.taskCardTop}>
            <div className={styles.taskTitleRow}>
              <Icon iconName="Clock" size={20} />
              <span className={styles.taskTitle}>{task.title}</span>
            </div>
            <div className={styles.taskCadenceRow}>
              <span className={styles.taskCadence}>{cadenceSummary}</span>
            </div>
          </div>

          {/* Checkbox row */}
          <div className={styles.taskCardBottom}>
            <CheckBox
              name="also-delete-conversations"
              label={t('chat:scheduledTaskDetail.alsoDeleteConversations')}
              checked={alsoDeleteConversations}
              onChange={(_, e) => setAlsoDeleteConversations(e.target.checked)}
            />
          </div>
        </div>
      </div>
    </Modal>
  );
};
